/*
 * File: c:\Users\j.mazat\Desktop\Moby\MobyFleet\mobyfleet_frontoffice\src\Content\Car\Pricing\PricingHolder.tsx
 * Project: c:\Users\j.mazat\Desktop\Moby\MobyFleet\mobyfleet_frontoffice
 * Created Date: Tuesday August 27th 2024
 * Author: Jonathan Mazat
 * -----
 * Last Modified: Tuesday August 27th 2024 2:23:42 pm
 * Modified By: the developer formerly known as Jonathan Mazat at <you@you.you>
 * -----
 * Copyright (c) 2024 Autobuy SAS
 * -----
 */

import { Box, Tab, Tabs, Typography } from "@mui/material";
import translator from "MultiLanguage/TranslationWrapper";
import { MouseEvent, useEffect, useMemo, useRef, useState } from "react";
import QuantitySelector from "./QuantitySelector";
import MileageDurationSelector from "./MileageDurationSelector";
import { PriceListsByType, PricingType } from "Types/pricing.type";
import PricingService from "Services/Pricing/PricingService";
import { getThemeColor } from "Utils/ThemeColor";
import PriceList from "../PriceList/priceList";
import { formatPricing } from "Utils/Pricing";
import HrStyled from "Theme/Component/HrStyled";
import Styles from "Content/Car/Pricing/Slider/pricing.module.css";
import SpanStyled from "Theme/Component/PricingSpan";
import ModalReservation from "Content/Modals/ModalReservation/ModalReservation";
import ButtonAlt from "Theme/Component/ButtonAlt";
import { Link } from "react-router-dom";
import Config from "Config/Config";
import OfferService from "Services/Offer/OfferService";
import ModalConfirmation from "Content/Modals/ModalConfirmation/ModalConfirmation";
import { a11yProps, CustomTabPanel } from "Utils/Tabs";

type PricingHolderProps = {
    id: string,
    themeColor: string,
    brand: string
};

export type hightlightType = {
    LLD: { mileage: number, month: number},
    LOA: { mileage: number, month: number},
};

export default function PricingHolder(props: PricingHolderProps) {
    const [pricingData, setPricingData] = useState<PricingType[]>([]);
    const lld = pricingData.filter(x => x.Type == "LLD");
    const loa = pricingData.filter(x => x.Type == "LOA");
    const [quantitySelected, setQuantitySelected] = useState<number>(1);
    const [tabIdx, setTabIdx] = useState(0);
    const themeColor = getThemeColor(props.themeColor);
    const [modalResa, setModalResa] = useState(false);
    const [modalConfirm, setModalConfirm] = useState(false);
    const [isOkay, setIsOkay] = useState(false);
    const pricingTable = useRef<PriceListsByType>({LLD: {}, LOA: {}});
    const [hightlight, setHightlight] = useState<hightlightType>({
        LOA: {mileage: 0, month: 0},
        LLD: {mileage: 0, month: 0}
    });
    const infoRequest= `mailto:${Config.COMMON.CONTACTEMAIL}?subject=${translator.decode("contact.subject").replaceAll("%i", props.brand.toUpperCase())}&body=${translator.decode("contact.body").replaceAll("%i", props.brand.toUpperCase()).replaceAll("%a", window.location)}`;

    useEffect(() => {
        PricingService.getPricing(props.id).then((result) => {
            setPricingData(result.data);
            const pricing = formatPricing(result.data);
            pricingTable.current = pricing;
            const hightlight = {
                LLD: {mileage: 0, month: 0},
                LOA: {mileage: 0, month: 0}
            };
            let tmp = Object.entries(pricing.LLD);
            if (tmp.length == 0) {
                setTabIdx(1);
            } else {
                hightlight.LLD.mileage = parseInt(tmp[0][0]);
                hightlight.LLD.month = parseInt(Object.keys(tmp[0][1])[0]);
            }
            tmp = Object.entries(pricing.LOA);
            if (tmp) {
                hightlight.LOA.mileage = parseInt(tmp[0][0]);
                hightlight.LOA.month = parseInt(Object.keys(tmp[0][1])[0]);
            }
            setHightlight(hightlight);
        }).catch((error) => {
            console.log(error.message);
        });
    }, []);

    const whatType = () => {
        var curType : "LLD" | "LOA";
        if (tabIdx === 1)
            curType = "LOA";
        else
            curType = "LLD";
        return curType;
    };

    const handleChange = (event: React.SyntheticEvent, idx: number) => {
        setTabIdx(idx);
    };

    const handleToggleCtaModal = (e: MouseEvent<HTMLElement>) => {
        setModalResa(false);
    };

    const handleMakeReservation = () => {
        if (filteredData) {
            var offerData = {
                ...filteredData,
                Quantity: quantitySelected
            };
            OfferService.makeReservation(offerData).then(() => {
                setIsOkay(true);
                setModalConfirm(true);
                setModalResa(false);
            }).catch(() => {
                setIsOkay(false);
                setModalConfirm(true);
                setModalResa(false);
            });
        }
    };
    const handleToggleModalConfirm = (e: MouseEvent<HTMLElement>) => {
        setModalConfirm(false);
    };
    const filteredData = useMemo(() => {
        const curType = whatType();
        return pricingData.find(item => item.Mileage === hightlight[curType].mileage && item.Month === hightlight[curType].month && item.Type === curType);
    }, [hightlight, tabIdx]);

    return (
        <>
            <Typography variant="h4" style={{ margin: '0 auto 1rem' }}>
                {
                    filteredData?
                    <p className="m-0">
                        {`${translator.decode("pricing.rent")}: `}<SpanStyled bgColor={props.themeColor}>{`${filteredData.Amount * quantitySelected}`} {`${translator.decode("pricing.amount")}`}</SpanStyled>
                    </p>
                    :
                    translator.decode("pricing.noPrice")
                }
            </Typography>
            <HrStyled bgColor={props.themeColor}/>
            <Box sx={{ width: '100%' }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={tabIdx} onChange={handleChange} variant="fullWidth" aria-label="Tabs to display LLD or LOA pricing" TabIndicatorProps={{style: {backgroundColor: themeColor}}}>
                        <Tab disabled={lld.length == 0} style={{color: lld.length == 0 ? "lightgrey" : themeColor}} label={translator.decode("pricing.LLD")} {...a11yProps(0, "pricing")} />
                        <Tab disabled={loa.length == 0} style={{color: loa.length == 0 ? "lightgrey" : themeColor}} label={translator.decode("pricing.LOA")} {...a11yProps(1, "pricing")} />
                    </Tabs>
                </Box>
                <CustomTabPanel value={tabIdx} index={0} idlabel={"pricing-tab-LLD"} aria={"pricing-tab-LLD"}>
                    <MileageDurationSelector data={lld} type={"LLD"} themeColor={props.themeColor} hightlight={hightlight} setter={setHightlight} />
                    <PriceList setup={pricingTable.current.LLD} type={"LLD"} themeColor={props.themeColor} hightlight={hightlight} setter={setHightlight} />
                </CustomTabPanel>
                <CustomTabPanel value={tabIdx} index={1} idlabel={"pricing-tab-LOA"} aria={"pricing-tab-LOA"}>
                    <MileageDurationSelector data={loa} type={"LOA"} themeColor={props.themeColor} hightlight={hightlight} setter={setHightlight} />
                    <PriceList setup={pricingTable.current.LOA} type={"LOA"} themeColor={props.themeColor} hightlight={hightlight} setter={setHightlight} />
                </CustomTabPanel>
            </Box>
            <QuantitySelector themeColor={props.themeColor} setter={setQuantitySelected} />
            <br/>
            <HrStyled bgColor={props.themeColor}/>
            <br/>
            <div className={`${Styles.posbtnAlt}`}>
                <ButtonAlt bgColor={props.themeColor} minWidth={"35%"} onClick={()=> setModalResa(true)} type="submit">{translator.decode("carElem.reservation")}</ButtonAlt>
                <Link to={infoRequest} className={`${Styles.link}`}>
                    <ButtonAlt bgColor={props.themeColor} minWidth={"35%"} noOutline >
                        {translator.decode("dynamic.callback")}
                    </ButtonAlt>
                </Link>
            </div>
            <ModalReservation makeReservation={handleMakeReservation} data={filteredData} nbCar={quantitySelected} show={modalResa} onClose={handleToggleCtaModal} closeModal={handleToggleCtaModal} themeColor={props.themeColor} />
            <ModalConfirmation show={modalConfirm} onClose={handleToggleModalConfirm} closeModal={handleToggleModalConfirm} themeColor={props.themeColor} isOkay={isOkay} />
        </>
    );
};
